import {
  demoCopyActivitySchema, demoInsightsSchema, type DemoInsights, demoCopyListSchema, myDemoCopySchema,
  type DemoCopyActivity, type DemoCopyList, type MyDemoCopy,
} from '@altsui/shared';
import { z } from 'zod';
import { api } from './client';

const sentInviteSchema = z.object({ inviteId: z.string(), email: z.string(), emailError: z.string().optional() });
export type SentDemoInvite = z.infer<typeof sentInviteSchema>;
const openedCopySchema = z.object({ fundId: z.string().min(1), status: z.enum(['preparing', 'ready', 'failed', 'removed']) });
export type OpenedDemoCopy = z.infer<typeof openedCopySchema>;

type Reader<T> = { safeParse(value: unknown): { success: true; data: T } | { success: false } };

function parsed<T>(schema: Reader<T>, value: unknown, what: string): T {
  const result = schema.safeParse(value);
  if (!result.success) throw new Error(`Lionshare sent ${what} it could not read.`);
  return result.data;
}

export const demoCopiesApi = {
  /** The signed-in person's copy when the current workspace is one; null otherwise, and for any answer that is not a copy. */
  mine: async (): Promise<MyDemoCopy | null> => {
    const result = myDemoCopySchema.safeParse(await api.get<unknown>('/demo-copies/mine'));
    return result.success ? result.data : null;
  },
  /** Throws the current copy away and makes a fresh one; the answer names the new workspace. */
  resetMine: async (): Promise<OpenedDemoCopy> => parsed(openedCopySchema, await api.post<unknown>('/demo-copies/mine/reset'), 'a reset'),
  /** A template manager makes (or reopens) their own copy. */
  claimMine: async (): Promise<OpenedDemoCopy> => parsed(openedCopySchema, await api.post<unknown>('/demo-copies/mine/claim'), 'a copy'),
  list: async (): Promise<DemoCopyList> => parsed(demoCopyListSchema, await api.get<unknown>('/demo-copies'), 'a list of copies'),
  activity: async (copyId: string): Promise<DemoCopyActivity> =>
    parsed(demoCopyActivitySchema, await api.get<unknown>(`/demo-copies/${encodeURIComponent(copyId)}/activity`), 'activity'),
  reset: async (copyId: string): Promise<OpenedDemoCopy> =>
    parsed(openedCopySchema, await api.post<unknown>(`/demo-copies/${encodeURIComponent(copyId)}/reset`), 'a reset'),
  /** Invites someone to the demo (the template), from wherever the manager is: their own copy is built now. */
  invite: async (email: string): Promise<SentDemoInvite> => parsed(sentInviteSchema, await api.post<unknown>('/demo-copies/invites', { email }), 'an invite'),
  /** Revokes a pending invite; the copy it built goes with it. */
  revokeInvite: async (inviteId: string): Promise<void> => { await api.post<unknown>(`/demo-copies/invites/${encodeURIComponent(inviteId)}/revoke`); },
  /** A page of the person's own copy was on screen (Demo insights). */
  recordView: async (beat: { page: string; visitId: string; seconds: number }, fundId: string): Promise<void> => {
    await api.post<unknown>('/demo-copies/mine/views', beat, { headers: { 'x-fund-id': fundId }, keepalive: true });
  },
  /** A note left with Tell us, kept with the page it was written on. */
  sendFeedback: async (page: string, text: string): Promise<void> => { await api.post<unknown>('/demo-copies/mine/feedback', { page, text }); },
  insights: async (): Promise<DemoInsights> => parsed(demoInsightsSchema, await api.get<unknown>('/demo-copies/insights'), 'demo insights'),
  remove: async (copyId: string): Promise<void> => { await api.post<unknown>(`/demo-copies/${encodeURIComponent(copyId)}/remove`); },
};
